
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Video from 'plaid-threads/Icons/Video';
import Libraries from 'plaid-threads/Icons/Libraries';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  layout: 'guide',
  description: 'Learn about Document Income features and implementation',
  section: 'Income',
  title: 'Document Income'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Document Income' subtitle={metadata.description} mdxType="Header" />
    <Actions.SmallWrapper miniTitle='Get started with Document Income'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/income/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='https://github.com/plaid/income-sample' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Document Income allows you to retrieve gross and/or net income information via user-uploaded documents such as a pay stub, bank statement, W-2, or 1099 form.`}</p>
    <YouTubeEmbed videoId='Hc-MpibSxJE' alignment='full' title='Plaid Income in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Income works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <Callout mdxType="Callout">
      <p>{`The flow below describes the latest integration pattern for Plaid Income. If you are an existing customer who integrated before August 17, 2026, your API calls have a slightly different interface. For details on the differences, see `}<a parentName="p" {...{
          "href": "/docs/api/users/user-apis/"
        }}>{`New User APIs`}</a>{`.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`(Optional) Update your `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/link/manualVerificationOfIncomeUpload"
        }}>{`Link customization`}</a>{` in the Dashboard to configure which types of documents a user can upload to verify their income. By default, if you do not change this setting, only pay stubs are accepted.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{` to create a `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` that will represent the end user interacting with your application. This step will only need to be done once per end user. If you are using multiple Income types, do not repeat this step when switching to a different Income type.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`. In addition to the required parameters, you will need to provide the following:`}
        <ul parentName="li">
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`user_id`}</inlineCode>{`, provide the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` from `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`products`}</inlineCode>{`, use `}<inlineCode parentName="li">{`["income_verification"]`}</inlineCode>{`. Document Income cannot be used in the same Link session as any other Plaid products, except for Payroll Income.`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`income_verification.income_source_types`}</inlineCode>{`, use `}<inlineCode parentName="li">{`payroll`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`(Optional) If you are only using Document Income and do not want customers to use Payroll Income, for `}<inlineCode parentName="li">{`income_verification.payroll_income.flow_types`}</inlineCode>{`, use `}<inlineCode parentName="li">{`["payroll_document_income"]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URI with the endpoint where you will receive Plaid webhooks.`}</li>
          <li parentName="ul">{`If using Fraud Risk, set `}<inlineCode parentName="li">{`income_verification.payroll_income.parsing_config`}</inlineCode>{` to either `}<inlineCode parentName="li">{`['risk_signals']`}</inlineCode>{` or `}<inlineCode parentName="li">{`['risk_signals', 'ocr']`}</inlineCode>{`. For more details, see `}<a parentName="li" {...{
              "href": "#fraud-risk-detection"
            }}>{`Fraud Risk detection`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`On the client side, create an instance of Link using the `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`; for more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`.`}</li>
      <li parentName="ol">{`Open Link in your web or mobile client and listen to the `}<inlineCode parentName="li">{`onSuccess`}</inlineCode>{` and `}<inlineCode parentName="li">{`onExit`}</inlineCode>{` callbacks, which will fire once the user has finished or exited the Link session.`}</li>
      <li parentName="ol">{`If using Fraud Risk, wait for the `}<a parentName="li" {...{
          "href": "/docs/api/products/income/#user_income_verification_risk_signals"
        }}><inlineCode parentName="a">{`INCOME: USER_INCOME_VERIFICATION_RISK_SIGNALS`}</inlineCode></a>{` webhook, then call `}<inlineCode parentName="li">{`/credit/payroll_income/risk_signals/get`}</inlineCode>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`If the document requires manual review, call `}<inlineCode parentName="li">{`/credit/payroll_income/get`}</inlineCode>{` to get a URI where the user's original documents can be downloaded.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`If using Document Parsing, wait for the `}<a parentName="li" {...{
          "href": "/docs/api/products/income/#user_income_verification"
        }}><inlineCode parentName="a">{`INCOME: USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook, then call `}<inlineCode parentName="li">{`/credit/payroll_income/get`}</inlineCode>{` and/or `}<inlineCode parentName="li">{`/credit/bank_statements/uploads/get`}</inlineCode>{` to obtain parsed income details. You can use `}<inlineCode parentName="li">{`/credit/sessions/get`}</inlineCode>{` to see what document types were uploaded, which will determine which of the two endpoints to call. For details, see `}<a parentName="li" {...{
          "href": "#document-parsing"
        }}>{`Document Parsing`}</a>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "fraud-risk-detection"
    }}>{`Fraud risk detection`}</h4>
    <p>{`To detect potential document fraud or document tampering during the Document Income flow, you can use the optional Fraud Risk feature. Fraud Risk's AI-powered analysis scans for over two dozen different fraud signals within categories such as: visual evidence of tampering, suspicious metadata, inconsistent contents, and similarity to known fraudulent documents. Fraud Risk is available for all supported document types, including pay stubs, 1099s, I-20s, and LES (Leave and Earnings Statement) documents. If your account is not enabled for this feature, contact sales or your Plaid account manager to request access.`}</p>
    <p>{`To enable Fraud Risk:`}</p>
    <ol>
      <li parentName="ol">{`When calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`, set the `}<inlineCode parentName="li">{`income_verification.payroll_income.parsing_config`}</inlineCode>{` array to include `}<inlineCode parentName="li">{`'risk_signals'`}</inlineCode>{`. By default, this will disable `}<a parentName="li" {...{
          "href": "#document-parsing"
        }}>{`Document Parsing`}</a>{`; to keep it enabled, include `}<inlineCode parentName="li">{`'ocr'`}</inlineCode>{` in the array as well.`}</li>
      <li parentName="ol">{`When the risk analysis has been completed, you will receive a `}<a parentName="li" {...{
          "href": "/docs/api/products/income/#user_income_verification_risk_signals"
        }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION_RISK_SIGNALS`}</inlineCode></a>{` webhook. This webhook may take up to 45 minutes to fire.`}</li>
      <li parentName="ol">{`Once the webhook has been received, call the `}<inlineCode parentName="li">{`/credit/payroll_income/risk_signals/get`}</inlineCode>{` endpoint.`}</li>
    </ol>
    <p><inlineCode parentName="p">{`/credit/payroll_income/risk_signals/get`}</inlineCode>{` will return a risk score for each document, as well as a detailed set of reasons for any potential risk.`}</p>
    <p>{`You can automatically reject documents with a high risk score, automatically accept documents with a low risk score, and manually review documents in between. We suggest starting with a threshold of 80 for auto-rejection and 20 for auto-acceptance. As you gather more data points on typical risk scores for your use case, you can tune these parameters to reduce the number of documents undergoing manual review.`}</p>
    <p>{`To obtain a copy of the original document for manual review, call `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{` and use the `}<inlineCode parentName="p">{`document_metadata[].download_url`}</inlineCode>{`.`}</p>
    <p>{`To enable Fraud Risk on a verification where the Link flow has already been completed, use the `}<inlineCode parentName="p">{`/credit/payroll_income/parsing_config/update`}</inlineCode>{` endpoint.`}</p>
    <h4 {...{
      "id": "document-parsing"
    }}>{`Document Parsing`}</h4>
    <p>{`Document Parsing is an optional feature that allows you to obtain a JSON representation of an uploaded document. If your account is not enabled for this feature, contact sales or your Plaid account manager to request access.`}</p>
    <p>{`To enable Document Parsing:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` as normal, then launch Link and have the user go through the Link flow. You do not need to specify a `}<inlineCode parentName="p">{`parsing_config`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, as Document Parsing will be enabled by default if this field is omitted. However, if this field is supplied (for example, to enable Fraud Risk), it must include `}<inlineCode parentName="p">{`ocr`}</inlineCode>{` to enable Document Parsing.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`To see which file types a user uploaded, use `}<inlineCode parentName="p">{`/credit/sessions/get`}</inlineCode>{`. The `}<inlineCode parentName="p">{`document_income_results`}</inlineCode>{` field will show how many of each file type were uploaded.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Wait for document parsing to complete, which will be indicated by the `}<a parentName="p" {...{
            "href": "/docs/api/products/income/#user_income_verification"
          }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook. This webhook typically fires within several minutes, but may take up to 45 minutes.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the webhook has been received, to obtain parsed JSON data from a bank statement, use `}<inlineCode parentName="p">{`/credit/bank_statements/uploads/get`}</inlineCode>{`. For all other `}<a parentName="p" {...{
            "href": "#supported-document-types"
          }}>{`supported document types`}</a>{`, use `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    <p>{`To enable Document Parsing on a verification where the Link flow has already been completed, use the `}<inlineCode parentName="p">{`/credit/payroll_income/parsing_config/update`}</inlineCode>{` endpoint.`}</p>
    <h4 {...{
      "id": "downloading-original-documents"
    }}>{`Downloading original documents`}</h4>
    <p>{`To download the original user-uploaded documents, use the `}<inlineCode parentName="p">{`document_metadata.download_url`}</inlineCode>{` returned by `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "customizing-document-uploads"
    }}>{`Customizing document uploads`}</h4>
    <p>{`To configure which documents your users can upload, create a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Link customization in the Dashboard`}</a>{`. You must update this setting in order to accept documents other than pay stubs.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Bank statements are only supported with a custom contract. If you are not contracted for bank statements, do not enable bank statement uploads in your Link customization, as it will cause your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` calls to fail. To request access to bank statement uploads, contact sales or your account manager.`}</p>
    </Callout>
    <h5 {...{
      "id": "supported-document-types"
    }}>{`Supported document types`}</h5>
    <ul>
      <li parentName="ul">{`Pay stubs`}</li>
      <li parentName="ul">{`Bank statements (requires custom contract)`}</li>
      <li parentName="ul">{`W-2`}</li>
      <li parentName="ul">{`1099-K`}</li>
      <li parentName="ul">{`1099-MISC`}</li>
      <li parentName="ul">{`I-20`}</li>
      <li parentName="ul">{`LES (Leave and Earnings Statement)`}</li>
    </ul>
    <Callout warning mdxType="Callout">
      <p>{`Each uploaded file must contain exactly one instance of the document type being submitted. For example, if uploading a pay stub, the file should contain a single pay stub — not multiple pay stubs combined into one file.`}</p>
    </Callout>
    <h5 {...{
      "id": "supported-file-types"
    }}>{`Supported file types`}</h5>
    <ul>
      <li parentName="ul">{`PDF`}</li>
      <li parentName="ul">{`PNG`}</li>
      <li parentName="ul">{`JPEG`}</li>
      <li parentName="ul">{`GIF`}</li>
      <li parentName="ul">{`BMP`}</li>
      <li parentName="ul">{`TIFF`}</li>
    </ul>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/customization/#document-income-only-document-upload-settings"
      }}>{`Document upload settings`}</a>{`.`}</p>
    <h4 {...{
      "id": "testing-document-income"
    }}>{`Testing Document Income`}</h4>
    <p>{`In the Sandbox environment, when testing Document Income, by default, the contents of the actual document will not be processed and Sandbox will instead use pre-populated test data. You can customize the response you get by uploading JSON files with a custom configuration schema in the Link flow. Each JSON file will represent one document in the response and can include customizations for:`}</p>
    <ul>
      <li parentName="ul">{`Either a pay stub or a W-2`}</li>
      <li parentName="ul">{`Risk signals`}</li>
      <li parentName="ul">{`OCR parsing status`}</li>
      <li parentName="ul">{`Risk signals status`}</li>
    </ul>
    <p>{`The customization schema for each document and risk signals is very similar to the objects in the responses from `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{` and `}<inlineCode parentName="p">{`/credit/payroll_income/risk_signals/get`}</inlineCode>{`. Example JSON files that you can adapt can be found in the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/sandbox-custom-users/tree/main/income/document_income"
      }}>{`Custom Sandbox User GitHub repo`}</a>{`.`}</p>
    <h4 {...{
      "id": "document-income-pricing"
    }}>{`Document Income pricing`}</h4>
    <p>{`Document Income is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#one-time-fee"
      }}>{`one-time fee model`}</a>{`. The fee depends on the number and type of documents processed and which processing options are enabled (i.e. fraud risk, document parsing, or both).`}</p>
    <p>{`To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle='See next steps to launch in Production' link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "fraud-risk-detection",
        level: 4,
        title: "Fraud risk detection",
      },
{
        id: "document-parsing",
        level: 4,
        title: "Document Parsing",
      },
{
        id: "downloading-original-documents",
        level: 4,
        title: "Downloading original documents",
      },
{
        id: "customizing-document-uploads",
        level: 4,
        title: "Customizing document uploads",
      },
{
        id: "testing-document-income",
        level: 4,
        title: "Testing Document Income",
      },
{
        id: "document-income-pricing",
        level: 4,
        title: "Document Income pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
